import React, { useState, useEffect } from 'react';
import {
  Button, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';

const { Option } = Select;
const { TextArea } = Input;

const emptySupplierRow = () => ({
  id: Date.now() + Math.random(),
  supplierName: null,
  avgItemPrice: 0,
});

/**
 * SaveAsNewProductModal
 *
 * Allows a Procurement user to register a free-text inquiry item as a new
 * catalogue Generic Product, plus one Supplier Product entry (with an
 * average item price) per supplier row. On confirm it calls onSave(data)
 * with `{ code, name, categoryId, description, supplierRows }` — the
 * caller is responsible for creating the Generic Product and the
 * corresponding Supplier Product records.
 *
 * Props:
 *   open          boolean
 *   onCancel      () => void
 *   onSave        (data) => void
 *   initialName   string — pre-filled from the inquiry item's free-text name
 *   categories    ProductCategory[]
 *   suppliers     Supplier[]
 *   existingProducts  Product[]  — used for duplicate-name check
 */
export default function SaveAsNewProductModal({
  open,
  onCancel,
  onSave,
  initialName = '',
  categories = [],
  suppliers = [],
  existingProducts = [],
}) {
  const { t } = useI18n();

  const [code, setCode] = useState('');
  const [name, setName] = useState(initialName);
  const [categoryId, setCategoryId] = useState(null);
  const [description, setDescription] = useState('');
  const [supplierRows, setSupplierRows] = useState([emptySupplierRow()]);

  const activeCategories = categories.filter((c) => c.status !== 'Inactive');
  const activeSuppliers = suppliers.filter((s) => s.status !== 'Inactive');

  // Sync name when initialName prop changes (new item opened)
  useEffect(() => {
    if (open) {
      setCode('');
      setName(initialName || '');
      setCategoryId(null);
      setDescription('');
      setSupplierRows([emptySupplierRow()]);
    }
  }, [open, initialName]);

  const handleAddSupplierRow = () => {
    setSupplierRows((prev) => [...prev, emptySupplierRow()]);
  };

  const handleRemoveSupplierRow = (id) => {
    setSupplierRows((prev) => {
      if (prev.length <= 1) return prev; // keep at least one row
      return prev.filter((r) => r.id !== id);
    });
  };

  const handleSupplierRowChange = (id, field, value) => {
    setSupplierRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    );
  };

  const handleConfirm = () => {
    // Validate name
    if (!name.trim()) {
      message.warning(t('Please enter product name'));
      return;
    }
    // Validate category
    if (!categoryId) {
      message.warning(t('Please select a category'));
      return;
    }
    // Validate supplier rows
    const validRows = supplierRows.filter((r) => r.supplierName);
    if (validRows.length === 0) {
      message.warning(t('At least one supplier is required'));
      return;
    }

    // Check for duplicate name
    const duplicate = existingProducts.find(
      (p) => p.name.toLowerCase().trim() === name.toLowerCase().trim(),
    );
    if (duplicate) {
      message.warning(
        `${t('A product with this name already exists')}: "${duplicate.name}". ${t('Consider using the existing product.')}`,
      );
      return;
    }

    onSave?.({
      code: code.trim() || undefined,
      name: name.trim(),
      categoryId,
      description: description.trim(),
      status: 'Active',
      supplierRows: validRows.map((r) => ({
        supplierName: r.supplierName,
        avgItemPrice: r.avgItemPrice || 0,
      })),
    });
    onCancel?.();
  };

  return (
    <Modal
      title={t('Save as New Product')}
      open={open}
      onCancel={onCancel}
      width={720}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Cancel')}</Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          {t('Create Product')}
        </Button>,
      ]}
      destroyOnClose={false}
    >
      <Form layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label={t('Product code')} extra={t('Optional')}>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. BAG-TOT"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label={t('Product name')} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('Please enter product name')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Category')} required>
              <Select
                placeholder={t('Select category')}
                value={categoryId}
                onChange={setCategoryId}
                showSearch
                optionFilterProp="children"
              >
                {activeCategories.map((c) => (
                  <Option key={c.id} value={c.id}>{c.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t('Description')}>
              <TextArea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('Product description')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider>{t('Suppliers')}</Divider>

        {supplierRows.map((row, index) => (
          <Row key={row.id} gutter={12} align="middle" style={{ marginBottom: 8 }}>
            <Col xs={24} sm={13}>
              <Form.Item label={index === 0 ? t('Supplier Name') : ''} style={{ marginBottom: 0 }}>
                <Select
                  placeholder={t('Select active supplier')}
                  value={row.supplierName}
                  onChange={(val) => handleSupplierRowChange(row.id, 'supplierName', val)}
                  showSearch
                  optionFilterProp="children"
                  allowClear
                >
                  {activeSuppliers.map((s) => (
                    <Option key={s.id} value={s.name}>{s.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={20} sm={8}>
              <Form.Item label={index === 0 ? t('Average Item Price') : ''} style={{ marginBottom: 0 }}>
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  value={row.avgItemPrice}
                  onChange={(val) => handleSupplierRowChange(row.id, 'avgItemPrice', val ?? 0)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={4} sm={3} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 0 }}>
              <Form.Item label={index === 0 ? ' ' : ''} style={{ marginBottom: 0 }}>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={supplierRows.length <= 1}
                  onClick={() => handleRemoveSupplierRow(row.id)}
                />
              </Form.Item>
            </Col>
          </Row>
        ))}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddSupplierRow}
          style={{ marginTop: 8 }}
        >
          {t('Add New Supplier')}
        </Button>
      </Form>
    </Modal>
  );
}
